import * as z from "zod";

// =============================================================================
// Tools config (step: oe-customer-actions)
// =============================================================================

const OrderEditingToolsConfigSchema_ = z.object({
  cancelOrder: z.boolean().optional(),
  changeAddress: z.boolean().optional(),
  updateOrder: z.boolean().optional(),
  updateOrderAddItem: z.boolean().optional(),
  updateOrderRemoveItem: z.boolean().optional(),
  updateOrderChangeVariant: z.boolean().optional(),
  addOrderDiscount: z.boolean().optional(),
  addOrderGiftNote: z.boolean().optional(),
});
export const OrderEditingToolsConfigSchema: z.ZodType<OrderEditingToolsConfig> =
  OrderEditingToolsConfigSchema_;
export interface OrderEditingToolsConfig extends z.infer<
  typeof OrderEditingToolsConfigSchema_
> {}

// =============================================================================
// Time/fulfillment config (step: oe-timing)
// =============================================================================

const DISALLOW_EVENTS = [
  "label_created",
  "packing_slip_printed",
  "pick_session_created",
] as const;

const FULFILLMENT_SYSTEMS = [
  "shopify",
  "shipstation",
  "shiphero",
  "other",
] as const;

const HOLD_STATUS_STRATEGIES = [
  "shopify_fulfillment_hold",
  "shopify_order_tag",
] as const;

const OrderEditingTimeConfigSchema_ = z.object({
  useOmsEvents: z.boolean().optional(),
  unifiedTimeWindowMinutes: z.number().optional(),
  selectedDisallowEvent: z.enum(DISALLOW_EVENTS).nullable().optional(),
  fulfillmentSystem: z.enum(FULFILLMENT_SYSTEMS).optional(),
  holds: z
    .object({
      applyHoldToAllOrders: z.boolean().optional(),
      holdDurationInMinutes: z.number().optional(),
      holdOrdersRequiringPayment: z.boolean().optional(),
      holdStatusStrategy: z.enum(HOLD_STATUS_STRATEGIES).optional(),
    })
    .optional(),
});
export const OrderEditingTimeConfigSchema: z.ZodType<OrderEditingTimeConfig> =
  OrderEditingTimeConfigSchema_;
export interface OrderEditingTimeConfig extends z.infer<
  typeof OrderEditingTimeConfigSchema_
> {}

// =============================================================================
// Platforms config (step: oe-platforms)
// =============================================================================

const OrderEditingPlatformsConfigSchema_ = z.object({
  shopperAI: z.boolean().optional(),
  supportSelfServe: z.boolean().optional(),
  orderTracking: z.boolean().optional(),
});
export const OrderEditingPlatformsConfigSchema: z.ZodType<OrderEditingPlatformsConfig> =
  OrderEditingPlatformsConfigSchema_;
export interface OrderEditingPlatformsConfig extends z.infer<
  typeof OrderEditingPlatformsConfigSchema_
> {}

// =============================================================================
// Full completion payload (assembled by client, sent via completeOnboarding)
// =============================================================================

const OrderEditingCompletionPayloadSchema_ = z.object({
  tools: OrderEditingToolsConfigSchema,
  timeConfig: OrderEditingTimeConfigSchema,
  enabledPlatforms: OrderEditingPlatformsConfigSchema.optional(),
  portalLogo: z.string().nullable().optional(),
});
export const OrderEditingCompletionPayloadSchema: z.ZodType<OrderEditingCompletionPayload> =
  OrderEditingCompletionPayloadSchema_;
export interface OrderEditingCompletionPayload extends z.infer<
  typeof OrderEditingCompletionPayloadSchema_
> {}

// =============================================================================
// Product availability (returned by getProgress, consumed by steps)
// =============================================================================

const OrderEditingAvailabilitySchema_ = z.object({
  shopperAI: z.boolean(),
  supportSelfServe: z.boolean(),
  orderTracking: z.boolean(),
  newOmsEnabled: z.boolean(),
});
export const OrderEditingAvailabilitySchema: z.ZodType<OrderEditingAvailability> =
  OrderEditingAvailabilitySchema_;
export interface OrderEditingAvailability extends z.infer<
  typeof OrderEditingAvailabilitySchema_
> {}

// =============================================================================
// Default values (used by useStepFormData fallback)
// =============================================================================

export const DEFAULT_TOOLS_CONFIG: OrderEditingToolsConfig = {
  cancelOrder: true,
  changeAddress: true,
  updateOrder: true,
  updateOrderAddItem: true,
  updateOrderRemoveItem: true,
  updateOrderChangeVariant: true,
  addOrderDiscount: true,
};

export const DEFAULT_TIME_CONFIG: OrderEditingTimeConfig = {
  useOmsEvents: false,
  unifiedTimeWindowMinutes: 60,
  selectedDisallowEvent: null,
  fulfillmentSystem: "shopify",
};

export const DEFAULT_PLATFORMS_CONFIG: OrderEditingPlatformsConfig = {
  shopperAI: true,
  supportSelfServe: true,
  orderTracking: true,
};
